import React from 'react';
import { Card, KPI, SectionBadge } from '@/components/UI';
import { formatNumber, formatDate } from '@/lib/format';

// Operations KPI export (hr-kpi-monthly) sections that have no home in the
// legacy Production JSON schema. Each card renders only when its block is
// present and carries a figure; an "unavailable" block, or one that is all
// zeros, stays hidden so a unit with no data is not padded with empty cards.

const inr = (n) => (n === null || n === undefined || Number.isNaN(Number(n)) ? '–'
  : '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 }));
const pct = (p) => (p === null || p === undefined ? '–' : `${Number(p).toFixed(1)}%`);
const num = (n) => (n === null || n === undefined ? '–' : formatNumber(Math.round(Number(n))));
const kg = (n) => (n === null || n === undefined ? '–' : `${formatNumber(Math.round(Number(n)))} kg`);
const hasAny = (obj, keys) => keys.some((k) => Number(obj?.[k]) > 0);
const available = (b) => b && typeof b === 'object' && (b.status === undefined || b.status === 'available');
const tone = (p) => (p >= 95 ? 'green' : p >= 80 ? 'amber' : 'red');

function Stat({ label, value, strong }) {
  return (
    <div className="border border-[var(--aampl-border)] rounded px-3 py-2 flex items-baseline justify-between gap-3 text-xs">
      <span className="text-slate-600">{label}</span>
      <span className={strong ? 'font-bold aampl-navy-text' : 'font-semibold aampl-navy-text'}>{value}</span>
    </div>
  );
}

function StatGrid({ items, cols = 4 }) {
  const cls = cols === 3 ? 'lg:grid-cols-3' : cols === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-4';
  return (
    <div className={`grid grid-cols-2 ${cls} gap-3`}>
      {items.map((it) => <Stat key={it.label} {...it} />)}
    </div>
  );
}

const Source = ({ children }) => <span className="text-xs text-slate-500">{children}</span>;
const Note = ({ children }) => <div className="text-[11px] text-slate-500 mt-2">{children}</div>;
const SubTitle = ({ children }) => <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{children}</div>;

// ---------------------------------------------------------------------------

export function ManpowerProductivityCard({ om }) {
  if (!om) return null;
  const keys = ['strokes_per_operator_hour', 'welding_strokes_per_operator_hour', 'pieces_per_operator_hour',
    'pieces_per_inspector_hour', 'operators_required', 'welding_operators_required', 'inspectors_required'];
  if (!hasAny(om, keys)) return null;
  const n1 = (v) => (v === null || v === undefined ? '–' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 1 }));
  return (
    <Card
      title="Manpower Productivity"
      action={<Source>From Operations KPI export • basis: {om.productivity_basis || 'plan'}</Source>}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3" data-testid="productivity-kpis">
        <KPI label="Strokes / Operator-hr (press)" value={n1(om.strokes_per_operator_hour)} status="navy" />
        <KPI label="Strokes / Operator-hr (welding)" value={n1(om.welding_strokes_per_operator_hour)} status="navy" />
        <KPI label="Pcs / Operator-hr (machine shop)" value={n1(om.pieces_per_operator_hour)} status="navy" />
        <KPI label="Pcs / Inspector-hr (final insp.)" value={n1(om.pieces_per_inspector_hour)} status="navy" />
      </div>
      <StatGrid cols={3} items={[
        { label: 'Operators required', value: n1(om.operators_required) },
        { label: 'Welding operators required', value: n1(om.welding_operators_required) },
        { label: 'Inspectors required', value: n1(om.inspectors_required) },
      ]} />
      <Note>Derived from the monthly net plan. Operations has no attendance or deployment actuals, so actual output per person cannot be computed.</Note>
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function ScheduleSupplyCard({ om }) {
  const svs = om?.schedule_vs_supply;
  const jw = om?.jobwork;
  const hasSvs = svs && hasAny(svs, ['monthly_total', 'sched_to_date', 'dispatched', 'parts']);
  const hasJw = jw && hasAny(jw, ['monthly_total', 'sched_to_date', 'received', 'sent', 'parts']);
  const hasBop = om && hasAny(om, ['bop_required_qty', 'bop_net_received_qty']);
  if (!hasSvs && !hasJw && !hasBop) return null;
  return (
    <Card title="Schedule vs Supply, Job-work & BOP" action={<Source>From Operations KPI export</Source>}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4" data-testid="svs-kpis">
        <KPI label="Schedule vs Supply (month)" value={pct(om.schedule_vs_supply_pct)} status={tone(om.schedule_vs_supply_pct)} />
        <KPI label="Job-work Compliance (month)" value={pct(om.jobwork_compliance_pct)} status={tone(om.jobwork_compliance_pct)} />
        <KPI label="BOP Supply Compliance (month)" value={pct(om.bop_supply_compliance_pct)} status={tone(om.bop_supply_compliance_pct)} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {hasSvs && (
          <div data-testid="svs-block">
            <SubTitle>Customer schedule vs dispatch</SubTitle>
            <div className="space-y-2">
              <Stat label="Monthly schedule" value={num(svs.monthly_total)} />
              <Stat label="Schedule to date" value={num(svs.sched_to_date)} />
              <Stat label="Dispatched" value={num(svs.dispatched)} strong />
              <Stat label="Pending" value={num(svs.pending)} />
              <Stat label="To-date %" value={pct(svs.pct_to_date)} />
              <Stat label="Parts scheduled" value={num(svs.parts)} />
              <Stat label="Dispatch value" value={inr(svs.dispatch_value_inr)} strong />
            </div>
          </div>
        )}
        {hasJw && (
          <div data-testid="jobwork-block">
            <SubTitle>Job-work (plating)</SubTitle>
            <div className="space-y-2">
              <Stat label="Monthly schedule" value={num(jw.monthly_total)} />
              <Stat label="Schedule to date" value={num(jw.sched_to_date)} />
              <Stat label="Sent" value={num(jw.sent)} />
              <Stat label="Received" value={num(jw.received)} strong />
              <Stat label="Pending" value={num(jw.pending)} />
              <Stat label="To-date %" value={pct(jw.pct_to_date)} />
              <Stat label="Parts" value={num(jw.parts)} />
            </div>
          </div>
        )}
        {hasBop && (
          <div data-testid="bop-block">
            <SubTitle>Bought-out parts (BOP)</SubTitle>
            <div className="space-y-2">
              <Stat label="Required qty" value={num(om.bop_required_qty)} />
              <Stat label="Net received" value={num(om.bop_net_received_qty)} strong />
              <Stat label="To-date %" value={pct(om.bop_supply_to_date_pct)} />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function InventoryCard({ om }) {
  const inv = om?.inventory;
  const neg = om?.negative_inventory;
  const hasInv = available(inv) && hasAny(inv, ['parts_tracked', 'total_pipeline_pcs']);
  const hasNeg = neg && hasAny(neg, ['open_issues', 'total_negative_pcs']);
  if (!hasInv && !hasNeg) return null;
  const issues = (neg?.issues || []).slice(0, 10);
  return (
    <Card
      title="Inventory Pipeline"
      action={<Source>From Operations KPI export{inv?.as_of ? ` • as of ${formatDate(inv.as_of)}` : ''}</Source>}
    >
      {hasInv && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="inventory-kpis">
          <KPI label="In-process" value={num(inv.in_process_pcs)} status="navy" />
          <KPI label="At plater" value={num(inv.at_plater_pcs)} status="navy" />
          <KPI label="Finished goods" value={num(inv.finished_goods_pcs)} status="navy" />
          <KPI label="Total pipeline" value={num(inv.total_pipeline_pcs)} status="navy" />
        </div>
      )}
      {hasInv && (
        <StatGrid cols={2} items={[
          { label: 'Parts tracked', value: num(inv.parts_tracked) },
          { label: 'Parts with a negative stage', value: num(inv.parts_with_negative_stage) },
        ]} />
      )}
      {hasNeg && (
        <div className="mt-4" data-testid="negative-inventory">
          <div className="flex items-center justify-between mb-2">
            <SubTitle>Negative stock issues</SubTitle>
            <SectionBadge status={neg.important_issues > 0 ? 'red' : neg.open_issues > 0 ? 'amber' : 'green'}
              label={`${neg.open_issues} open • ${neg.important_issues} important`} />
          </div>
          <StatGrid cols={3} items={[
            { label: 'Total negative pcs', value: num(neg.total_negative_pcs), strong: true },
            ...Object.entries(neg.by_stage || {}).map(([s, c]) => ({ label: `${s.replace(/_/g, ' ')} issues`, value: num(c) })),
          ]} />
          {issues.length > 0 && (
            <div className="aampl-table-wrap mt-3">
              <table className="aampl-table text-xs" data-testid="negative-inventory-table">
                <thead><tr><th>Part</th><th>Operation</th><th>Stage</th><th className="text-right">Negative Qty</th><th>Responsible</th></tr></thead>
                <tbody>
                  {issues.map((i, k) => (
                    <tr key={k}>
                      <td className="font-medium">{i.part_name || '-'}{i.part_no && <div className="text-[10px] text-slate-500">{i.part_no}</div>}</td>
                      <td>{i.operation || '-'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{(i.stage || '-').replace(/_/g, ' ')}</td>
                      <td className="text-right font-bold text-red-700">{num(i.negative_qty)}</td>
                      <td style={{ textTransform: 'capitalize' }}>{i.responsible_role || '-'}{i.important ? ' • important' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {neg.last_scan_at && <Note>Snapshot of the last Inventory Assistant scan ({formatDate(neg.last_scan_at)}).</Note>}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function HoldMaterialCard({ om }) {
  const h = om?.hold_material;
  if (!available(h) || !hasAny(h, ['opening_hold_pcs', 'added_pcs', 'closing_hold_pcs', 'recovered_pcs', 'scrapped_pcs'])) return null;
  const parts = [...(h.parts || [])].sort((a, b) => (b.closing_hold || 0) - (a.closing_hold || 0)).slice(0, 8);
  return (
    <Card title="Hold Material (Rework)" action={<Source>From Operations KPI export • rework monthly review</Source>}>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-3" data-testid="hold-kpis">
        <KPI label="Opening hold" value={num(h.opening_hold_pcs)} status="navy" />
        <KPI label="Added" value={num(h.added_pcs)} status="amber" />
        <KPI label="Recovered" value={num(h.recovered_pcs)} status="green" />
        <KPI label="Scrapped" value={num(h.scrapped_pcs)} status="red" />
        <KPI label="Closing hold" value={num(h.closing_hold_pcs)} status={h.closing_hold_pcs > h.opening_hold_pcs ? 'amber' : 'green'} />
      </div>
      <StatGrid cols={2} items={[
        { label: 'Closing hold value', value: inr(h.value_inr), strong: true },
        { label: 'Parts on hold', value: num((h.parts || []).length) },
      ]} />
      {parts.length > 0 && (
        <div className="aampl-table-wrap mt-3">
          <table className="aampl-table text-xs" data-testid="hold-parts-table">
            <thead><tr><th>Part</th><th>Customer</th><th className="text-right">Opening</th><th className="text-right">Added</th><th className="text-right">Recovered</th><th className="text-right">Closing</th><th className="text-right">Value</th></tr></thead>
            <tbody>
              {parts.map((p, k) => (
                <tr key={k}>
                  <td className="font-medium">{p.part_name || '-'}{p.part_no && <div className="text-[10px] text-slate-500">{p.part_no}</div>}</td>
                  <td>{p.customer_name || '-'}</td>
                  <td className="text-right">{num(p.opening_hold)}</td>
                  <td className="text-right">{num(p.added)}</td>
                  <td className="text-right">{num(p.recovered)}</td>
                  <td className="text-right font-bold">{num(p.closing_hold)}</td>
                  <td className="text-right">{inr(p.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(h.parts || []).length > parts.length && <Note>Top {parts.length} of {(h.parts || []).length} parts by closing hold.</Note>}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function PlanningCard({ om }) {
  const p = om?.planning;
  if (!available(p) || !(p.fg_parts_total > 0)) return null;
  return (
    <Card title="FG Planning Cover" action={<Source>From Operations KPI export{p.as_of ? ` • as of ${formatDate(p.as_of)}` : ''}</Source>}>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="planning-kpis">
        <KPI label="Critical" value={num(p.fg_parts_critical)} status={p.fg_parts_critical > 0 ? 'red' : 'green'} />
        <KPI label="Warning" value={num(p.fg_parts_warning)} status={p.fg_parts_warning > 0 ? 'amber' : 'green'} />
        <KPI label="OK" value={num(p.fg_parts_ok)} status="green" />
        <KPI label="Stale" value={num(p.fg_parts_stale)} status="navy" />
        <KPI label="No schedule" value={num(p.fg_parts_no_schedule)} status="navy" />
      </div>
      <Note>{num(p.fg_parts_total)} parts; critical = negative FG or under {p.fg_threshold_days} days of cover; warning = under {2 * (p.fg_threshold_days || 0)} days; stale = no dispatch in 14 days.</Note>
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function PlatingAssemblyCard({ om }) {
  const pq = om?.plating_quality;
  const asm = om?.assembly;
  const hasPq = available(pq) && hasAny(pq, ['made_pcs', 'crack_ng_pcs']);
  const hasAsm = available(asm) && hasAny(asm, ['assembly_parts', 'plan_qty', 'produced_pcs']);
  if (!hasPq && !hasAsm) return null;
  return (
    <Card title="Plating Quality & Assembly" action={<Source>From Operations KPI export</Source>}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {hasPq && (
          <div data-testid="plating-block">
            <SubTitle>Plating (job-work received)</SubTitle>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <KPI label="Made pcs" value={num(pq.made_pcs)} status="navy" />
              <KPI label="Crack NG pcs" value={num(pq.crack_ng_pcs)} status={pq.crack_ng_pcs > 0 ? 'amber' : 'green'} />
              <KPI label="Crack NG %" value={pq.crack_ng_percent === null || pq.crack_ng_percent === undefined ? '–' : `${Number(pq.crack_ng_percent).toFixed(3)}%`} status="navy" />
            </div>
            {Object.keys(pq.crack_ng_by_department || {}).length > 0 && (
              <StatGrid cols={2} items={Object.entries(pq.crack_ng_by_department).map(([d, v]) => ({ label: `Caught at ${d.replace(/_/g, ' ')}`, value: num(v) }))} />
            )}
          </div>
        )}
        {hasAsm && (
          <div data-testid="assembly-block">
            <SubTitle>Assembly</SubTitle>
            <div className="grid grid-cols-3 gap-3">
              <KPI label="Assembly parts" value={num(asm.assembly_parts)} status="navy" />
              <KPI label="Plan qty" value={num(asm.plan_qty)} status="navy" />
              <KPI label="Produced" value={num(asm.produced_pcs)} status={asm.plan_qty > 0 ? tone(asm.produced_pcs / asm.plan_qty * 100) : 'navy'} />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function StoreGodownCard({ om }) {
  const s = om?.store_godown;
  if (!available(s) || !(s.total_balance_pcs > 0 || Object.keys(s.by_bucket || {}).length > 0)) return null;
  const labels = { rtp: 'Return to production', rework_plating: 'Awaiting re-plating', rts: 'Return to supplier' };
  const buckets = Object.entries(s.by_bucket || {});
  return (
    <Card title="Store Godown" action={<Source>From Operations KPI export • live balance{s.as_of ? ` as of ${formatDate(s.as_of)}` : ''}</Source>}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
        <KPI label="Total balance" value={num(s.total_balance_pcs)} status="navy" />
      </div>
      {buckets.length > 0 && (
        <div className="aampl-table-wrap">
          <table className="aampl-table text-xs" data-testid="store-godown-table">
            <thead><tr><th>Bucket</th><th className="text-right">Parts</th><th className="text-right">In</th><th className="text-right">Out</th><th className="text-right">Balance</th></tr></thead>
            <tbody>
              {buckets.map(([k, b]) => (
                <tr key={k}>
                  <td className="font-medium">{labels[k] || k}</td>
                  <td className="text-right">{num(b.parts)}</td>
                  <td className="text-right">{num(b.in_pcs)}</td>
                  <td className="text-right">{num(b.out_pcs)}</td>
                  <td className="text-right font-bold">{num(b.balance_pcs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Note>Store Godown keeps a live running balance only; there is no month-end snapshot.</Note>
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function RawMaterialCard({ om }) {
  const rm = om?.raw_material;
  if (!available(rm) || !hasAny(rm, ['parts_tracked', 'scheduled_parts', 'rm_stock_kg'])) return null;
  const shorts = (rm.short_parts_top || []).slice(0, 8);
  return (
    <Card title="Raw Material Planning" action={<Source>From Operations KPI export{rm.as_of ? ` • as of ${formatDate(rm.as_of)}` : ''}</Source>}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3" data-testid="rm-kpis">
        <KPI label="Parts short of RM" value={num(rm.parts_short)} status={rm.parts_short > 0 ? 'red' : 'green'} />
        <KPI label="To procure" value={kg(rm.to_procure_kg)} status={rm.to_procure_kg > 0 ? 'amber' : 'green'} />
        <KPI label="RM stock" value={kg(rm.rm_stock_kg)} status="navy" />
        <KPI label="RM stock value" value={inr(rm.rm_value_inr)} status="navy" />
      </div>
      <StatGrid items={[
        { label: 'Parts tracked', value: num(rm.parts_tracked) },
        { label: 'Scheduled parts', value: num(rm.scheduled_parts) },
        { label: 'Net requirement', value: kg(rm.net_requirement_kg_total) },
        { label: 'Parts missing RM data', value: num(rm.rm_data_missing_parts) },
      ]} />
      {shorts.length > 0 && (
        <div className="aampl-table-wrap mt-3">
          <table className="aampl-table text-xs" data-testid="rm-short-table">
            <thead><tr><th>Part</th><th className="text-right">Schedule pcs</th><th className="text-right">RM required</th><th className="text-right">Rate ₹/kg</th></tr></thead>
            <tbody>
              {shorts.map((p, k) => (
                <tr key={k}>
                  <td className="font-medium">{p.part_name || '-'}{p.part_no && <div className="text-[10px] text-slate-500">{p.part_no}</div>}</td>
                  <td className="text-right">{num(p.schedule_pcs)}</td>
                  <td className="text-right font-bold">{kg(p.rm_required_kg)}</td>
                  <td className="text-right">{p.rm_rate_inr_per_kg === null || p.rm_rate_inr_per_kg === undefined ? '–' : Number(p.rm_rate_inr_per_kg).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Note>Top {shorts.length} parts short of raw material, by RM required.</Note>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

const OBJECTIVE_LABELS = {
  daily_plan_vs_actual: 'Daily plan vs actual',
  production_per_manpower: 'Production per manpower',
  safety_on_shop_floor: 'Safety on shop floor',
  documentation_on_shop_floor: 'Documentation on shop floor',
  in_house_rejection: 'In-house rejection',
  suggestion_kaizen_poka_yoke: 'Suggestion / Kaizen / Poka-yoke',
  five_s: '5S',
  daily_production_report: 'Daily production report',
};
const statusTone = (s) => (s === 'available' ? 'green' : s === 'proxy' ? 'amber' : 'red');

function objectiveFigures(key, o) {
  const f = [];
  const add = (l, v) => { if (v !== null && v !== undefined && v !== '') f.push(`${l}: ${v}`); };
  switch (key) {
    case 'daily_plan_vs_actual':
      add('Press plan', num(o.press_plan_qty)); add('Press actual', num(o.press_actual_qty));
      if (o.machine_plan_qty) { add('Machine plan', num(o.machine_plan_qty)); add('Machine actual', num(o.machine_actual_qty)); }
      break;
    case 'production_per_manpower':
      add('Strokes/operator-hr (plan)', o.strokes_per_operator_hour_plan); add('M/shop pcs/operator-hr (plan)', o.mshop_pcs_per_operator_hour_plan); break;
    case 'documentation_on_shop_floor':
      add('PM check sheets filled', num(o.pm_checksheets_filled)); add('Days with production entries', num(o.days_with_production_entries)); break;
    case 'in_house_rejection':
      add('Red tag qty', num(o.red_tag_qty)); add('Red tag value', inr(o.red_tag_value_inr)); add('Rejection PPM', num(o.rejection_ppm)); break;
    case 'suggestion_kaizen_poka_yoke':
      add('Improvement records', num(o.improvement_maintenance_records)); break;
    case 'daily_production_report':
      add('Days reported', `${num(o.days_with_production_entries)} / ${num(o.days_in_month)}`); break;
    default: break;
  }
  return f;
}

export function ObjectivesCard({ om }) {
  const obj = om?.objectives;
  if (!obj || Object.keys(obj).length === 0) return null;
  return (
    <Card title="Monthly Objectives" action={<Source>From Operations KPI export • objective evidence</Source>}>
      <div className="aampl-table-wrap">
        <table className="aampl-table text-xs" data-testid="objectives-table">
          <thead><tr><th>Objective</th><th>Status</th><th>Figures</th><th>Note</th></tr></thead>
          <tbody>
            {Object.entries(obj).map(([k, o]) => (
              <tr key={k}>
                <td className="font-medium">{OBJECTIVE_LABELS[k] || k.replace(/_/g, ' ')}</td>
                <td><SectionBadge status={statusTone(o?.status)} label={o?.status || '-'} /></td>
                <td>{objectiveFigures(k, o || {}).map((t) => <div key={t}>{t}</div>)}</td>
                <td className="text-slate-500" style={{ whiteSpace: 'normal', maxWidth: 420 }}>{o?.note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Note>Status: available = measured directly; proxy = nearest recorded evidence; unavailable = Operations captures no data for it.</Note>
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function OpsSnapshotSection({ om }) {
  if (!om) return null;
  return (
    <>
      <ScheduleSupplyCard om={om} />
      <InventoryCard om={om} />
      <HoldMaterialCard om={om} />
      <PlanningCard om={om} />
      <RawMaterialCard om={om} />
      <PlatingAssemblyCard om={om} />
      <StoreGodownCard om={om} />
      <ObjectivesCard om={om} />
    </>
  );
}
